import React, { useState, useEffect } from "react";
import { X, CheckCircle2, Clock, Copy, Check, Building2, CreditCard, Landmark, ArrowUpRight, ShieldCheck } from "lucide-react";
import { MONTHS } from "../../constants/hrConstants";
import { hrService } from "../../services/hrService";
import { useHR } from "../../context/HRContext";
import toast from "react-hot-toast";

export function TransactionDetailsModal({ isOpen, onClose, payslip }) {
  const { employees } = useHR();
  const [transaction, setTransaction] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Look up employee bank details from blum_employees
  const employee = employees.find(
    (e) => String(e.id) === String(payslip?.employeeId) || String(e.employeeId) === String(payslip?.employeeId)
  );

  useEffect(() => {
    if (!isOpen || !payslip?.employeeId) return;
    hrService
      .getTransactionDetails(payslip.employeeId, { month: payslip.month, year: payslip.year })
      .then((res) => {
        setTransaction(res?.data?.data?.transaction || null);
      })
      .catch(() => {
        setTransaction(null);
      });
  }, [isOpen, payslip]);

  if (!isOpen || !payslip) return null;

  const monthName = MONTHS[Number(payslip.month) - 1] || payslip.month;
  const isPaid = payslip.status === "paid" || transaction?.status === "captured";

  // Account masking (last 4 digits)
  const bankName = employee?.bankName || employee?.bankDetails?.bankName;
  const accountHolderName = employee?.accountHolderName || employee?.bankDetails?.accountHolderName;
  const rawAccount = employee?.accountNumber || employee?.bankDetails?.accountNumber || "";
  const ifscCode = employee?.ifscCode || employee?.bankDetails?.ifscCode;
  const branchName = employee?.branchName || employee?.bankDetails?.branchName;
  const accountType = employee?.accountType || employee?.bankDetails?.accountType;

  const maskedAccount =
    rawAccount && String(rawAccount).length >= 4
      ? `•••• •••• ${String(rawAccount).slice(-4)}`
      : rawAccount || "Not Provided";

  const handleCopy = (text, type) => {
    if (!text || text === "N/A" || text === "Not Provided") return;
    navigator.clipboard.writeText(text);
    setCopiedId(type);
    toast.success(`Copied ${type}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const paymentDateStr =
    transaction?.paidAt || payslip.paidOn
      ? new Date(transaction?.paidAt || payslip.paidOn).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : new Date(payslip.generatedOn).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

  const paymentId = transaction?.razorpayPaymentId || "N/A";
  const orderId = transaction?.razorpayOrderId || "N/A";
  const referenceId = `rcpt_${payslip.employeeId}_${payslip.month}_${payslip.year}`;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={onClose} />

      {/* GPay Style Digital Receipt Container */}
      <div className="relative bg-white rounded-[28px] shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-slate-100 flex flex-col max-h-[92vh]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Scrollable Receipt Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          {/* Top Google Pay Status Header */}
          <div className="text-center pt-2 pb-1">
            {/* Initial Circle Avatar */}
            <div className="relative inline-block mb-3">
              <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-blue-600 via-indigo-600 to-violet-600 text-white flex items-center justify-center text-2xl font-black shadow-lg shadow-blue-500/25 border-4 border-white">
                {payslip.employeeName?.charAt(0)?.toUpperCase() || "E"}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-white p-0.5 rounded-full shadow">
                {isPaid ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 fill-emerald-500 stroke-white" />
                ) : (
                  <Clock className="w-6 h-6 text-amber-500 fill-amber-500 stroke-white" />
                )}
              </div>
            </div>

            {/* Paid Amount */}
            <div className="flex items-baseline justify-center gap-0.5 text-slate-900 font-extrabold text-3xl tracking-tight">
              <span className="text-2xl font-bold text-slate-500">₹</span>
              {Number(payslip.netPay).toLocaleString("en-IN")}
            </div>

            {/* Status Subtitle */}
            <div className="mt-2 flex items-center justify-center gap-1.5">
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase ${
                  isPaid
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                    : "bg-amber-50 text-amber-700 border border-amber-200/60"
                }`}
              >
                {isPaid ? "✓ Paid to" : "⏳ Pending for"} {payslip.employeeName}
              </span>
            </div>

            <p className="text-[11.5px] font-medium text-slate-400 mt-1.5">{paymentDateStr}</p>
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-slate-200" />

          {/* Card 1: Bank Account Details (Paid To) */}
          <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100 space-y-3">
            <div className="flex items-center gap-2 text-slate-500">
              <Landmark className="w-4 h-4 text-blue-600" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Bank Account Details</span>
            </div>

            <div className="space-y-2 text-[12.5px]">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Bank Name</span>
                <span className="font-semibold text-slate-800">{bankName || "Not Provided"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Account Holder</span>
                <span className="font-semibold text-slate-800">{accountHolderName || payslip.employeeName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Account Number</span>
                <span className="font-mono font-bold text-slate-800">{maskedAccount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">IFSC Code</span>
                <span className="font-mono font-semibold text-slate-800">{ifscCode || "Not Provided"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Branch Name</span>
                <span className="font-medium text-slate-700">{branchName || "Not Provided"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Account Type</span>
                <span className="font-medium text-slate-700 capitalize">{accountType || "Savings"}</span>
              </div>
            </div>
          </div>

          {/* Card 2: Transaction Details */}
          <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100 space-y-3">
            <div className="flex items-center gap-2 text-slate-500">
              <ArrowUpRight className="w-4 h-4 text-blue-600" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Transaction Details</span>
            </div>

            <div className="space-y-2.5 text-[12.5px]">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Salary Period</span>
                <span className="font-semibold text-slate-800">{monthName} {payslip.year}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Gross Salary</span>
                <span className="font-semibold text-slate-700">₹{Number(payslip.grossEarnings).toLocaleString("en-IN")}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Deductions</span>
                <span className="font-semibold text-red-500">-₹{Number(payslip.totalDeductions).toLocaleString("en-IN")}</span>
              </div>

              <div className="flex justify-between items-center border-t border-slate-200/60 pt-2">
                <span className="text-slate-500 font-bold">Net Transferred</span>
                <span className="font-extrabold text-emerald-600 text-sm">₹{Number(payslip.netPay).toLocaleString("en-IN")}</span>
              </div>

              {/* Payment ID with copy button */}
              <div className="flex justify-between items-center pt-1">
                <span className="text-slate-400 font-medium">Payment ID</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[11.5px] font-semibold text-slate-700">{paymentId}</span>
                  {paymentId !== "N/A" && (
                    <button
                      onClick={() => handleCopy(paymentId, "Payment ID")}
                      className="p-1 rounded hover:bg-slate-200/80 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Copy Payment ID"
                    >
                      {copiedId === "Payment ID" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Razorpay Order ID */}
              {orderId !== "N/A" && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 font-medium">Order ID</span>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[11.5px] font-semibold text-slate-700">{orderId}</span>
                    <button
                      onClick={() => handleCopy(orderId, "Order ID")}
                      className="p-1 rounded hover:bg-slate-200/80 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Copy Order ID"
                    >
                      {copiedId === "Order ID" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              )}

              {/* Reference ID */}
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-medium">Reference ID</span>
                <span className="font-mono text-[11.5px] text-slate-600">{referenceId}</span>
              </div>
            </div>
          </div>

          {/* Footer Security Note */}
          <div className="flex items-center justify-center gap-1.5 text-slate-400 text-[11px] pt-1">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
            <span>Encrypted & Verified by Verve Kitchens HR</span>
          </div>
        </div>
      </div>
    </div>
  );
}
